// src/components/AssetList/AssetList.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import './AssetList.css'; // Import CSS for styling
import { fetchClients } from '../../store/clientsSlice';
import { fetchAssets, updateAsset, deleteAsset } from '../../store/assetSlice';
import parseDateToUtcIso from '../../utils/date';
import {
  ACCOUNTING_DEPRECIATION_METHOD_OPTIONS,
  TAX_ASSET_BLOCK_OPTIONS,
  getOptionLabel,
} from '../../constants/taxOptions';

const toCanonicalDateInputValue = (value) => {
  if (!value) {
    return '';
  }
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date(value));
  } catch (error) {
    return '';
  }
};

const buildAssetPayload = (asset) => ({
  ...asset,
  purchasePrice: asset.purchasePrice === '' || asset.purchasePrice == null ? null : parseFloat(asset.purchasePrice),
  purchaseYear: asset.purchaseYear === '' || asset.purchaseYear == null ? 0 : parseInt(asset.purchaseYear, 10),
  putToUseDate: asset.putToUseDate ? parseDateToUtcIso(asset.putToUseDate) : null,
  annualDepreciation:
    asset.annualDepreciation === '' || asset.annualDepreciation == null ? null : parseFloat(asset.annualDepreciation),
  salvageValue: asset.salvageValue === '' || asset.salvageValue == null ? null : parseFloat(asset.salvageValue),
  usefulLife: asset.usefulLife === '' || asset.usefulLife == null ? 0 : parseInt(asset.usefulLife, 10),
  taxDepreciationRate:
    asset.taxDepreciationRate === '' || asset.taxDepreciationRate == null ? null : parseFloat(asset.taxDepreciationRate),
});

const AssetList = () => {
  const dispatch = useDispatch();
  const { clientId, financialYear } = useSelector((state) => state.app);
  const { assets, loading, error } = useSelector((state) => state.asset);

  const [editIndex, setEditIndex] = useState(null);
  const [editedAsset, setEditedAsset] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(fetchClients());
    dispatch(fetchAssets());
  }, [dispatch, clientId, financialYear]);

  const handleAddAsset = () => {
    navigate('/asset');
  };

  const handleEditClick = (index) => {
    setEditIndex(index);
    setEditedAsset({
      ...assets[index],
      putToUseDate: toCanonicalDateInputValue(assets[index].putToUseDate),
    });
  };

  const handleCancelEdit = () => {
    setEditIndex(null);
    setEditedAsset({});
  };

  const handleSaveEdit = (id) => {
    dispatch(updateAsset({ id, asset: buildAssetPayload(editedAsset) }))
      .then(() => {
        setEditIndex(null);
        setEditedAsset({});
      })
      .catch((err) => {
        console.error('Error updating asset', err);
      });
  };

  const handleDelete = (id) => {
    dispatch(deleteAsset(id))
      .catch((err) => {
        console.error('Error deleting asset', err);
      });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedAsset({ ...editedAsset, [name]: value });
  };

  return (
    <div className="asset-list-page">
      <h2>Asset List</h2>
      <p className="financial-page-subtitle">Review book and tax attributes together so depreciation reports and the balance sheet read from the same master data.</p>
      <div className="financial-toolbar">
        <button onClick={handleAddAsset} className="add-asset-button">Add Asset</button>
      </div>
      {error && <p className="financial-message financial-message--error">{error}</p>}
      {loading && <p className="financial-message">Loading assets...</p>}
      {assets.length > 0 && (
        <div className="table-container">
          <table className="asset-table">
            <thead>
              <tr>
                <th>Asset Name</th>
                <th>Purchase Price</th>
                <th>Purchase FY Start</th>
                <th>Put To Use Date</th>
                <th>Accounting Method</th>
                <th>Accounting WDV Rate</th>
                <th>Salvage Value</th>
                <th>Useful Life (years)</th>
                <th>Tax Block</th>
                <th>Tax Rate Override</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {assets.map((asset, index) => (
                <tr key={index}>
                  {editIndex === index ? (
                    <>
                      <td>
                        <input
                          type="text"
                          name="assetName"
                          value={editedAsset.assetName}
                          onChange={handleInputChange}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          name="purchasePrice"
                          value={editedAsset.purchasePrice}
                          onChange={handleInputChange}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          name="purchaseYear"
                          value={editedAsset.purchaseYear}
                          onChange={handleInputChange}
                        />
                      </td>
                      <td>
                        <input
                          type="date"
                          name="putToUseDate"
                          value={editedAsset.putToUseDate || ''}
                          onChange={handleInputChange}
                        />
                      </td>
                      <td>
                        <select
                          name="accountingDepreciationMethod"
                          value={editedAsset.accountingDepreciationMethod || 'WRITTEN_DOWN_VALUE'}
                          onChange={handleInputChange}
                        >
                          {ACCOUNTING_DEPRECIATION_METHOD_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          name="annualDepreciation"
                          value={editedAsset.annualDepreciation}
                          onChange={handleInputChange}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          name="salvageValue"
                          value={editedAsset.salvageValue}
                          onChange={handleInputChange}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          name="usefulLife"
                          value={editedAsset.usefulLife}
                          onChange={handleInputChange}
                        />
                      </td>
                      <td>
                        <select
                          name="taxAssetBlock"
                          value={editedAsset.taxAssetBlock || ''}
                          onChange={handleInputChange}
                        >
                          <option value="">Select tax block</option>
                          {TAX_ASSET_BLOCK_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          name="taxDepreciationRate"
                          value={editedAsset.taxDepreciationRate || ''}
                          onChange={handleInputChange}
                        />
                      </td>
                      <td>
                        <button className="financial-button" onClick={() => handleSaveEdit(asset.id)}>Save</button>
                        <button className="financial-button financial-button--secondary" onClick={handleCancelEdit}>Cancel</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{asset.assetName}</td>
                      <td>{asset.purchasePrice}</td>
                      <td>{asset.purchaseYear}</td>
                      <td>{toCanonicalDateInputValue(asset.putToUseDate) || '-'}</td>
                      <td>{getOptionLabel(ACCOUNTING_DEPRECIATION_METHOD_OPTIONS, asset.accountingDepreciationMethod, 'Written Down Value (WDV)')}</td>
                      <td>{asset.annualDepreciation}</td>
                      <td>{asset.salvageValue}</td>
                      <td>{asset.usefulLife}</td>
                      <td>{getOptionLabel(TAX_ASSET_BLOCK_OPTIONS, asset.taxAssetBlock)}</td>
                      <td>{asset.taxDepreciationRate ?? '-'}</td>
                      <td>
                        <button className="financial-button financial-button--secondary" onClick={() => handleEditClick(index)}>Edit</button>
                        <button className="financial-button financial-button--danger" onClick={() => handleDelete(asset.id)}>Delete</button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AssetList;
